import { initializeGraphicsProfiles, type GraphicsProfile } from "./graphicsState";
// CSS Cesium importé ici pour être chargé uniquement quand Cesium est utilisé
import "cesium/Build/Cesium/Widgets/widgets.css";

import * as Cesium from "cesium";

import {
  BasemapClipContour,
  BasemapLightingOptions,
  CameraOptions,
  CesiumViewerConfig,
  LoadedModel,
  LoadedModelMetadata,
  LocalModelAlignment,
  ModelArrivalAnimationOptions,
  ModelLoadOptions,
  SolarDrivenLightingOptions,
} from "./types";
import {
  COULEUR_LUMIERE_PAR_DEFAUT,
  FACTEUR_IBL_BASEMAP_LIT_PAR_DEFAUT,
  FACTEUR_IBL_BASEMAP_NUIT_PAR_DEFAUT,
  INTENSITE_LUMIERE_BASEMAP_LIT_PAR_DEFAUT,
  INTENSITE_LUMIERE_BASEMAP_NUIT_PAR_DEFAUT,
  INTENSITE_LUMIERE_PAR_DEFAUT,
  OMBRES_BASEMAP_PAR_DEFAUT,
  OMBRES_TERRAIN_PAR_DEFAUT,
  PLANCHER_LUMINEUX_BASEMAP_LIT_PAR_DEFAUT,
} from "./renderDefaults";
import {
  LocalizedFlattenTerrainProvider,
  ZoneAplatissementTerrain,
} from "./LocalizedFlattenTerrainProvider";

import { Season } from "@/stores/solarSimulationStore";
import { calculateSolarPosition } from "@/lib/solar/calculateSolarPosition";

/**
 * Gestionnaire centralisé pour Cesium
 * Singleton pattern pour s'assurer qu'une seule instance existe
 */
type BasemapLightingResolved = {
  iblDiffuse: number;
  iblSpecular: number;
  intensiteLumiereBasemap: number;
  plancherLumineux: number;
  signature: string;
};

const basemapLightingSignatureCache = new WeakMap<
  Cesium.Cesium3DTileset | Cesium.Model,
  string
>();
const solarLightingSignatureCache = new WeakMap<
  Cesium.Cesium3DTileset | Cesium.Model,
  string
>();
const shaderPlancherLumineuxCache = new Map<string, Cesium.CustomShader>();

function calculerFacteurSolairePartage(
  options?: BasemapLightingOptions,
): number {
  const solaire = options?.solaire;

  if (!solaire) {
    const attenuationNocturne = options?.attenuationNocturne;

    if (!attenuationNocturne?.actif) {
      return 1;
    }

    const heure = attenuationNocturne.hour ?? 12;
    const saison = attenuationNocturne.season ?? Season.SUMMER;
    const positionSolaire = calculateSolarPosition(heure, saison);
    return positionSolaire.daylightFactor;
  }

  return calculateSolarPosition(solaire.hour, solaire.season, solaire.anchor)
    .daylightFactor;
}

function calculerPlancherLumineuxDynamique(
  options?: BasemapLightingOptions,
): number {
  const configurationDynamique = options?.plancherLumineuxDynamique;

  if (!configurationDynamique?.actif) {
    return (
      options?.plancherLumineuxLit ?? PLANCHER_LUMINEUX_BASEMAP_LIT_PAR_DEFAUT
    );
  }

  const minimum = Math.max(0, configurationDynamique.minimum);
  const maximum = Math.max(minimum, configurationDynamique.maximum);
  const facteurSolaire = calculerFacteurSolairePartage(options);

  return Cesium.Math.lerp(minimum, maximum, facteurSolaire);
}

function calculerFacteurNocturne(options?: BasemapLightingOptions): number {
  const attenuationNocturne = options?.attenuationNocturne;

  if (!attenuationNocturne?.actif) {
    return 1;
  }

  return calculerFacteurSolairePartage(options);
}

function creerShaderPlancherLumineuxBasemap(
  plancherLumineuxLit?: number,
): Cesium.CustomShader | undefined {
  if (typeof plancherLumineuxLit !== "number" || plancherLumineuxLit <= 0) {
    return undefined;
  }

  const cleCache = plancherLumineuxLit.toFixed(3);
  const shaderExistant = shaderPlancherLumineuxCache.get(cleCache);
  if (shaderExistant) {
    return shaderExistant;
  }

  const shader = new Cesium.CustomShader({
    fragmentShaderText: `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material)
{
  material.emissive += clamp(material.diffuse * ${cleCache}, vec3(0.0), vec3(1.0));
}
    `,
  });

  shaderPlancherLumineuxCache.set(cleCache, shader);
  return shader;
}

function calculerParametresEclairageBasemap(
  options?: BasemapLightingOptions,
): BasemapLightingResolved {
  const facteurNocturne = calculerFacteurNocturne(options);
  const iblMaximum = Math.min(
    Math.max(
      options?.imageBasedLightingFactor?.diffuse ??
        FACTEUR_IBL_BASEMAP_LIT_PAR_DEFAUT,
      0,
    ),
    1,
  );
  const iblMinimum = Cesium.Math.clamp(
    options?.attenuationNocturne?.iblMinimum ??
      FACTEUR_IBL_BASEMAP_NUIT_PAR_DEFAUT,
    0,
    1,
  );
  const iblDiffuse = Cesium.Math.lerp(iblMinimum, iblMaximum, facteurNocturne);
  const iblSpecular = options?.imageBasedLightingFactor?.specular ?? 0.0;

  const intensiteLumiereMaximum =
    options?.lightColorIntensity ?? INTENSITE_LUMIERE_BASEMAP_LIT_PAR_DEFAUT;
  const intensiteLumiereMinimum = Math.max(
    0,
    options?.attenuationNocturne?.lightColorMinimum ??
      INTENSITE_LUMIERE_BASEMAP_NUIT_PAR_DEFAUT,
  );
  const intensiteLumiereBasemap = Cesium.Math.lerp(
    intensiteLumiereMinimum,
    intensiteLumiereMaximum,
    facteurNocturne,
  );
  const plancherLumineux = calculerPlancherLumineuxDynamique(options);
  const signature = [
    iblDiffuse.toFixed(3),
    iblSpecular.toFixed(3),
    intensiteLumiereBasemap.toFixed(3),
    plancherLumineux.toFixed(3),
  ].join("|");

  return {
    iblDiffuse,
    iblSpecular,
    intensiteLumiereBasemap,
    plancherLumineux,
    signature,
  };
}

function appliquerParametresEclairageBasemap(
  primitive: Cesium.Cesium3DTileset | Cesium.Model,
  options?: BasemapLightingOptions,
  forceRefresh: boolean = false,
): void {
  const parametres = calculerParametresEclairageBasemap(options);
  const signaturePrecedente = basemapLightingSignatureCache.get(primitive);

  if (!forceRefresh && signaturePrecedente === parametres.signature) {
    return;
  }

  basemapLightingSignatureCache.set(primitive, parametres.signature);

  const imageBasedLighting = primitive.imageBasedLighting;
  if (imageBasedLighting) {
    const facteurExistant = imageBasedLighting.imageBasedLightingFactor;
    if (facteurExistant) {
      facteurExistant.x = parametres.iblDiffuse;
      facteurExistant.y = parametres.iblSpecular;
    } else {
      imageBasedLighting.imageBasedLightingFactor = new Cesium.Cartesian2(
        parametres.iblDiffuse,
        parametres.iblSpecular,
      );
    }
  } else {
    const nouvelIbl = new Cesium.ImageBasedLighting();
    nouvelIbl.imageBasedLightingFactor = new Cesium.Cartesian2(
      parametres.iblDiffuse,
      parametres.iblSpecular,
    );
    primitive.imageBasedLighting = nouvelIbl;
  }

  const lightColor = primitive.lightColor;
  if (lightColor) {
    lightColor.x = parametres.intensiteLumiereBasemap;
    lightColor.y = parametres.intensiteLumiereBasemap;
    lightColor.z = parametres.intensiteLumiereBasemap;
  } else {
    primitive.lightColor = new Cesium.Cartesian3(
      parametres.intensiteLumiereBasemap,
      parametres.intensiteLumiereBasemap,
      parametres.intensiteLumiereBasemap,
    );
  }

  if ("customShader" in primitive) {
    primitive.customShader = creerShaderPlancherLumineuxBasemap(
      parametres.plancherLumineux,
    );
  }

  if ("environmentMapManager" in primitive && primitive.environmentMapManager) {
    primitive.environmentMapManager.brightness = 1;
    primitive.environmentMapManager.saturation = 1;
    primitive.environmentMapManager.gamma = 1;
    primitive.environmentMapManager.atmosphereScatteringIntensity = 1;
  }
}

function appliquerParametresEclairageSolaireModele(
  primitive: Cesium.Cesium3DTileset | Cesium.Model,
  options?: SolarDrivenLightingOptions,
  forceRefresh: boolean = false,
): void {
  if (!options) {
    return;
  }

  const positionSolaire = calculateSolarPosition(
    options.hour,
    options.season,
    options.anchor,
  );
  const intensiteMinimum = Math.max(0, options.intensiteLumiereMinimum ?? 0.08);
  const intensiteMaximum = Math.max(
    intensiteMinimum,
    options.intensiteLumiereMaximum ?? 1,
  );
  const facteurIblMinimum = Cesium.Math.clamp(
    options.facteurIblMinimum ?? 0,
    0,
    1,
  );
  const facteurIblMaximum = Cesium.Math.clamp(
    options.facteurIblMaximum ?? 1,
    facteurIblMinimum,
    1,
  );
  const intensiteLumiereBase = Cesium.Math.lerp(
    intensiteMinimum,
    intensiteMaximum,
    positionSolaire.daylightFactor * positionSolaire.daylightFactor,
  );
  const facteurIblBase = Cesium.Math.lerp(
    facteurIblMinimum,
    facteurIblMaximum,
    positionSolaire.twilightFactor,
  );
  const intensiteLumiere = Math.max(
    0,
    intensiteLumiereBase * (options.multiplicateurIntensiteLumiere ?? 1),
  );
  const facteurIbl = Cesium.Math.clamp(
    facteurIblBase * (options.multiplicateurIbl ?? 1),
    0,
    1,
  );
  const signature = [
    options.hour.toFixed(3),
    options.season,
    options.anchor?.latitude?.toFixed(6) ?? "default-lat",
    options.anchor?.longitude?.toFixed(6) ?? "default-lng",
    (options.multiplicateurIntensiteLumiere ?? 1).toFixed(3),
    (options.multiplicateurIbl ?? 1).toFixed(3),
    intensiteMinimum.toFixed(3),
    intensiteMaximum.toFixed(3),
    facteurIblMinimum.toFixed(3),
    facteurIblMaximum.toFixed(3),
    intensiteLumiere.toFixed(3),
    facteurIbl.toFixed(3),
  ].join("|");
  const signaturePrecedente = solarLightingSignatureCache.get(primitive);

  if (!forceRefresh && signaturePrecedente === signature) {
    return;
  }

  solarLightingSignatureCache.set(primitive, signature);

  const imageBasedLighting = primitive.imageBasedLighting;
  if (imageBasedLighting?.imageBasedLightingFactor) {
    imageBasedLighting.imageBasedLightingFactor.x = facteurIbl;
    imageBasedLighting.imageBasedLightingFactor.y = 0;
  } else {
    const nouvelIbl = imageBasedLighting ?? new Cesium.ImageBasedLighting();
    nouvelIbl.imageBasedLightingFactor = new Cesium.Cartesian2(facteurIbl, 0);
    primitive.imageBasedLighting = nouvelIbl;
  }

  const lightColor = primitive.lightColor;
  if (lightColor) {
    lightColor.x = intensiteLumiere;
    lightColor.y = intensiteLumiere;
    lightColor.z = intensiteLumiere;
  } else {
    primitive.lightColor = new Cesium.Cartesian3(
      intensiteLumiere,
      intensiteLumiere,
      intensiteLumiere,
    );
  }

  if ("environmentMapManager" in primitive && primitive.environmentMapManager) {
    primitive.environmentMapManager.brightness = intensiteLumiere;
    primitive.environmentMapManager.saturation = 1;
    primitive.environmentMapManager.gamma = 1;
    primitive.environmentMapManager.atmosphereScatteringIntensity = 1;
  }
}

class CesiumManager {
  // Coordonnées par défaut : Centre de la Suisse (latitude, longitude, altitude)
  // Utilisées uniquement si le projet ne définit pas de coordonnées spécifiques
  private static readonly DEFAULT_LATITUDE = 46.8;
  private static readonly DEFAULT_LONGITUDE = 8.2;
  // private static readonly SWISSTOPO_WMTS_URL =
  //   "https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857";
  private static readonly SWISSTOPO_WMTS_TEMPLATE =
    // EPSG:3857 (Web Mercator) - compatible avec WebMercatorTilingScheme de Cesium
    // Le endpoint 21781 (LV03) a un tiling matriciel propre a la Suisse, incompatible avec Cesium
    "https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg";
  private static readonly SWISSTOPO_TERRAIN_URL =
    "https://3d.geo.admin.ch/ch.swisstopo.terrain.3d/v1/";

  // Rayon de chargement du terrain autour du point de départ (en mètres)
  private static readonly TERRAIN_LOAD_RADIUS = 2000; // 2 km
  private static readonly DEFAULT_MODEL_READY_TIMEOUT_MS = 120000;
  private static readonly DEFAULT_TILESET_STREAM_TIMEOUT_MS = 120000;
  private static readonly TRANSIENT_LOAD_RETRY_COUNT = 1;
  private static readonly INITIAL_CAMERA_HEADING = 0.0;
  private static readonly INITIAL_CAMERA_PITCH_DEGREES = -50;

  private static instance: CesiumManager;
  private viewer: Cesium.Viewer | null = null;
  private loadedModels: Map<string, LoadedModel> = new Map();
  private terrainCenterPoint: Cesium.Cartesian3 | null = null;
  // Polygons de clipping appliqués au globe, indexés par modelId
  private globeClippingPolygons: Map<string, Cesium.ClippingPolygon[]> =
    new Map();
  private flattenTerrainProvider: LocalizedFlattenTerrainProvider | null = null;
  private terrainFlattenZones: Map<string, ZoneAplatissementTerrain> =
    new Map();
  private flattenTerrainDebugEnabled = false;
  private flattenTerrainDebugEntities: Map<string, Cesium.Entity[]> = new Map();
  private runningModelArrivalAnimations: Map<string, () => void> = new Map();
  private preserveDrawingBufferEnabled = false;
  private initialization: Promise<Cesium.Viewer> | null = null;
  private graphicsGeneration = 0;

  /**
   * Lit une durée (ms) depuis l'environnement Vite avec garde-fous.
   */
  private static resolveTimeoutMs(
    envKey:
      | "VITE_CESIUM_MODEL_READY_TIMEOUT_MS"
      | "VITE_CESIUM_TILESET_STREAM_TIMEOUT_MS",
    fallbackMs: number,
  ): number {
    const rawValue = import.meta.env[envKey];
    if (typeof rawValue !== "string" || rawValue.trim().length === 0) {
      return fallbackMs;
    }

    const parsed = Number.parseInt(rawValue, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      console.warn(
        `[CesiumManager] ${envKey} invalide ("${rawValue}"), fallback ${fallbackMs}ms`,
      );
      return fallbackMs;
    }

    return parsed;
  }

  private static getModelReadyTimeoutMs(): number {
    return CesiumManager.resolveTimeoutMs(
      "VITE_CESIUM_MODEL_READY_TIMEOUT_MS",
      CesiumManager.DEFAULT_MODEL_READY_TIMEOUT_MS,
    );
  }

  private static getTilesetStreamTimeoutMs(): number {
    return CesiumManager.resolveTimeoutMs(
      "VITE_CESIUM_TILESET_STREAM_TIMEOUT_MS",
      CesiumManager.DEFAULT_TILESET_STREAM_TIMEOUT_MS,
    );
  }

  private isPrimitiveInScene(
    primitive: Cesium.Cesium3DTileset | Cesium.Model,
  ): boolean {
    return Boolean(
      this.viewer &&
      !this.viewer.isDestroyed() &&
      this.viewer.scene.primitives.contains(primitive),
    );
  }

  private ensurePrimitiveStillValid(
    id: string,
    primitive: Cesium.Cesium3DTileset | Cesium.Model | null,
  ): asserts primitive is Cesium.Cesium3DTileset | Cesium.Model {
    if (!primitive) {
      throw new Error(
        `[CesiumManager] Primitive absente après chargement pour "${id}"`,
      );
    }

    if (primitive.isDestroyed?.()) {
      throw new Error(
        `[CesiumManager] Primitive détruite pendant le chargement de "${id}"`,
      );
    }

    if (!this.isPrimitiveInScene(primitive)) {
      throw new Error(
        `[CesiumManager] Primitive "${id}" absente de la scène après chargement`,
      );
    }
  }

  private isTransientLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    const lowered = message.toLowerCase();

    return (
      lowered.includes("timeout") ||
      lowered.includes("failed to fetch") ||
      lowered.includes("networkerror") ||
      lowered.includes("network error") ||
      lowered.includes("load vertex buffer") ||
      lowered.includes("draco decoder could not be initialized") ||
      lowered.includes("webassembly.instantiate(): out of memory") ||
      lowered.includes("primitive détruite") ||
      lowered.includes("absente de la scène")
    );
  }

  private async loadModelInternal(
    id: string,
    options: ModelLoadOptions,
  ): Promise<void> {
    let primitive: Cesium.Cesium3DTileset | Cesium.Model | null = null;
    let lastReportedProgress = 0;
    const reportProgress = (
      progress: number,
      determinate: boolean,
      stage: "preparation" | "streaming" | "finalization",
      pendingRequests?: number,
      pendingRequestsPeak?: number,
    ) => {
      const clampedProgress = Math.max(0, Math.min(1, progress));
      const monotonicProgress = Math.max(lastReportedProgress, clampedProgress);
      lastReportedProgress = monotonicProgress;
      options.onProgress?.({
        progress: monotonicProgress,
        determinate,
        stage,
        pendingRequests,
        pendingRequestsPeak,
      });
    };

    try {
      reportProgress(0.05, false, "preparation");

      const token = localStorage.getItem("token");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const resource = new Cesium.Resource({
        url: options.url,
        headers: headers,
      });
      reportProgress(0.1, false, "preparation");

      if (options.url.includes(".json")) {
        const customShader = options.renderUnlit
          ? new Cesium.CustomShader({
              lightingModel: Cesium.LightingModel.UNLIT,
            })
          : undefined;

        const tileset = await Cesium.Cesium3DTileset.fromUrl(resource, {
          maximumScreenSpaceError: 16,
          debugShowBoundingVolume: false,
          cacheBytes: 1536 * 1024 * 1024,
          maximumCacheOverflowBytes: 512 * 1024 * 1024,
          cullWithChildrenBounds: true,
          cullRequestsWhileMoving: true,
          cullRequestsWhileMovingMultiplier: 30.0,
          shadows: options.shadows ?? OMBRES_BASEMAP_PAR_DEFAUT,
          dynamicScreenSpaceError: true,
          foveatedScreenSpaceError: true,
          foveatedConeSize: 1.0,
          foveatedMinimumScreenSpaceErrorRelaxation: 0.0,
          foveatedTimeDelay: 0.0,
          preloadWhenHidden: false,
          preloadFlightDestinations: true,
          preferLeaves: false,
          progressiveResolutionHeightFraction: 0.3,
          skipLevelOfDetail: false,
          customShader,
        } as any);

        (tileset as any).maximumSimultaneousTileLoads = 12;
        appliquerParametresEclairageBasemap(tileset, options.basemapLighting);

        try {
          const scene = this.viewer!.scene;
          const requestRenderModeEnabled = Boolean(
            (scene as unknown as { requestRenderMode?: boolean })
              .requestRenderMode,
          );

          if (requestRenderModeEnabled) {
            const anyTileset = tileset as unknown as {
              tileLoadProgressEvent?: {
                addEventListener?: (
                  cb: (pendingRequests: number) => void,
                ) => () => void;
              };
            };

            const removeTileProgressListener =
              anyTileset.tileLoadProgressEvent?.addEventListener?.(
                (pendingRequests: number) => {
                  if (!Number.isFinite(pendingRequests)) {
                    return;
                  }

                  scene.requestRender();

                  if (pendingRequests > 0) {
                    return;
                  }

                  scene.requestRender();
                },
              );

            if (typeof removeTileProgressListener === "function") {
              const originalDestroy = tileset.destroy.bind(tileset);
              tileset.destroy = (() => {
                removeTileProgressListener();
                return originalDestroy();
              }) as typeof tileset.destroy;
            }
          }
        } catch (error) {
          console.warn(
            "[CesiumManager] Impossible d'installer le rafraîchissement 3D Tiles:",
            error,
          );
        }

        tileset.initialTilesLoaded.addEventListener(() => {
          if (options.clipGlobeTerrain) {
            const appliedPrecomputedClipping =
              this.applyPrecomputedGlobeTerrainClipping(
                options.clipGlobeTerrainContours ?? [],
                id,
              );

            if (!appliedPrecomputedClipping) {
              this.clipGlobeTerrainUnderTileset(tileset, id);
            }
          }
        });

        tileset.tileFailed.addEventListener(
          (error: { url?: string; message?: string }) => {
            console.error(
              `[CesiumManager] Tileset "${id}" tile failed:`,
              error.url,
              error.message,
            );
          },
        );
        this.viewer!.scene.primitives.add(tileset);
        primitive = tileset;
        reportProgress(0.12, false, "streaming");

        if (options.skipStreamingWait) {
          // En mode 3D Tiles natif, on peut considerer le chargement comme pret
          // des que la primitive est ajoutee a la scene. Le streaming initial
          // continue ensuite en arriere-plan sous la responsabilite de Cesium.
          reportProgress(0.98, false, "finalization");
        } else {
          await this.waitForTilesetInitialStreaming(
            tileset,
            id,
            (progress, determinate, pendingRequests, pendingRequestsPeak) => {
              reportProgress(
                progress,
                determinate,
                "streaming",
                pendingRequests,
                pendingRequestsPeak,
              );
            },
          );
          reportProgress(0.98, true, "finalization");
        }
      } else {
        const heading = Cesium.Math.toRadians(options.heading ?? 0);
        const pitch = Cesium.Math.toRadians(options.pitch ?? 0);
        const roll = Cesium.Math.toRadians(options.roll ?? 0);
        const hpr = new Cesium.HeadingPitchRoll(heading, pitch, roll);

        const incrementallyLoadTextures =
          options.incrementallyLoadTextures ?? true;
        const deferShowUntilTexturesReady =
          options.deferShowUntilTexturesReady ?? false;

        const model = await Cesium.Model.fromGltfAsync({
          url: resource,
          modelMatrix: options.position
            ? Cesium.Transforms.headingPitchRollToFixedFrame(
                options.position,
                hpr,
              )
            : Cesium.Matrix4.IDENTITY,
          scale: options.scale ?? 1.0,
          minimumPixelSize: options.minimumPixelSize ?? 0,
          maximumScale: options.maximumScale ?? undefined,
          incrementallyLoadTextures,
          shadows: options.shadows ?? Cesium.ShadowMode.ENABLED,
          customShader: options.applySdrGammaCorrection
            ? new Cesium.CustomShader({
                lightingModel: Cesium.LightingModel.UNLIT,
                fragmentShaderText: `
void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material)
{
  material.diffuse = pow(material.diffuse, vec3(1.0 / 2.2));
  material.emissive = pow(material.emissive, vec3(1.0 / 2.2));
}
                `,
              })
            : options.renderUnlit
              ? new Cesium.CustomShader({
                  lightingModel: Cesium.LightingModel.UNLIT,
                })
              : undefined,
        });

        appliquerParametresEclairageSolaireModele(
          model,
          options.solarLighting,
          true,
        );

        model.distanceDisplayCondition = new Cesium.DistanceDisplayCondition(
          0.0,
          Number.MAX_VALUE,
        );

        if (deferShowUntilTexturesReady) {
          model.show = false;
        }

        this.viewer!.scene.primitives.add(model);
        primitive = model;
        reportProgress(0.65, false, "streaming");

        try {
          const scene = this.viewer!.scene;
          const requestRenderModeEnabled = Boolean(
            (scene as unknown as { requestRenderMode?: boolean })
              .requestRenderMode,
          );
          const hasTexturesReadyFlag = "texturesReady" in (model as any);

          if (requestRenderModeEnabled && hasTexturesReadyFlag) {
            const anyModel = model as any;
            const removePostRender = scene.postRender.addEventListener(() => {
              if (model.isDestroyed?.()) {
                removePostRender();
                return;
              }
              if (anyModel.texturesReady === true) {
                removePostRender();
                return;
              }
              scene.requestRender();
            });

            scene.requestRender();

            if (anyModel.texturesReadyEvent?.addEventListener) {
              anyModel.texturesReadyEvent.addEventListener(() => {
                if (deferShowUntilTexturesReady && !model.isDestroyed?.()) {
                  model.show = true;
                }
                scene.requestRender();
              });
            }
          }
        } catch (error) {
          console.warn(
            "[CesiumManager] Impossible d'installer le rafraîchissement texturesReady:",
            error,
          );
        }

        if (deferShowUntilTexturesReady) {
          try {
            const anyModel = model as any;
            if (anyModel.texturesReady === true) {
              model.show = true;
              this.viewer!.scene.requestRender();
            } else if (anyModel.texturesReadyEvent?.addEventListener) {
              anyModel.texturesReadyEvent.addEventListener(() => {
                if (!model.isDestroyed?.()) {
                  model.show = true;
                  this.viewer!.scene.requestRender();
                }
              });
            } else {
              window.setTimeout(() => {
                if (
                  this.viewer &&
                  !this.viewer.isDestroyed() &&
                  !model.isDestroyed?.()
                ) {
                  model.show = true;
                  this.viewer.scene.requestRender();
                }
              }, 1500);
            }
          } catch {
            // Pas bloquant.
          }
        }

        await this.waitForModelReadyOrError(model, id);
        reportProgress(0.95, true, "finalization");
      }

      this.ensurePrimitiveStillValid(id, primitive);

      const metadata: LoadedModelMetadata = {
        kind: primitive instanceof Cesium.Cesium3DTileset ? "tileset" : "model",
        position: options.position
          ? {
              x: options.position.x,
              y: options.position.y,
              z: options.position.z,
            }
          : null,
        headingDeg: options.heading ?? 0,
        pitchDeg: options.pitch ?? 0,
        rollDeg: options.roll ?? 0,
        scale: options.scale ?? 1.0,
        submissionType: options.metadata?.submissionType,
        sourceLayerType: options.metadata?.sourceLayerType,
        sourceLayerId: options.metadata?.sourceLayerId,
      };

      this.loadedModels.set(id, {
        id,
        url: options.url,
        primitive,
        visible: true,
        opacity: 1.0,
        terrainFlattenEnabled: options.flattenTerrain ?? false,
        terrainFlattenContours: options.flattenTerrainContours ?? [],
        metadata,
      });

      if (options.flattenTerrain) {
        this.syncModelTerrainFlatten(id);
      }

      this.requestRender();
      reportProgress(1, true, "finalization");
    } catch (error) {
      try {
        if (
          primitive &&
          this.viewer &&
          !this.viewer.isDestroyed() &&
          !primitive.isDestroyed?.()
        ) {
          this.viewer.scene.primitives.remove(primitive);
        }
      } catch (cleanupError) {
        console.warn(
          `[CesiumManager] Nettoyage partiel du modèle "${id}" échoué:`,
          cleanupError,
        );
      }

      console.error(
        `[CesiumManager] Erreur lors du chargement du modèle "${id}":`,
        error,
      );
      throw error;
    }
  }

  /**
   * Applique exactement la pose caméra demandée, sans offset implicite.
   */
  private applyInitialCameraView(
    latitude: number,
    longitude: number,
    baseAltitude: number,
    pitchRadians: number = Cesium.Math.toRadians(
      CesiumManager.INITIAL_CAMERA_PITCH_DEGREES,
    ),
  ): void {
    if (!this.viewer) {
      return;
    }
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(
        longitude,
        latitude,
        baseAltitude,
      ),
      orientation: {
        heading: CesiumManager.INITIAL_CAMERA_HEADING,
        pitch: pitchRadians,
        roll: 0.0,
      },
    });
  }

  /**
   * Récupère l'instance singleton
   */
  static getInstance(): CesiumManager {
    if (!CesiumManager.instance) {
      CesiumManager.instance = new CesiumManager();
    }
    return CesiumManager.instance;
  }

  /**
   * Initialise le viewer Cesium
   * @param containerId - ID du conteneur HTML
   * @param config - Configuration optionnelle
   */
  initialize(containerId: string, config: CesiumViewerConfig = {}): Promise<Cesium.Viewer> {
    if (this.initialization) return this.initialization;
    if (this.isInitialized()) return Promise.resolve(this.getViewer());
    const generation = this.graphicsGeneration;
    const promise = initializeGraphicsProfiles(
      profile => this.initializeAttempt(containerId, config, profile, generation),
      () => {
        this.destroy();
        const container = document.getElementById(containerId);
        for (const canvas of container?.querySelectorAll("canvas") ?? []) {
          try {
            const context = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
            context?.getExtension("WEBGL_lose_context")?.loseContext();
          } catch {
            // Le pilote peut refuser toute opération sur un contexte partiel.
          }
        }
        container?.replaceChildren();
      },
      () => generation === this.graphicsGeneration,
    ).finally(() => {
      if (this.initialization === promise) this.initialization = null;
    });
    this.initialization = promise;
    return promise;
  }

  private async initializeAttempt(
    containerId: string,
    config: CesiumViewerConfig,
    profile: GraphicsProfile,
    generation: number,
  ): Promise<Cesium.Viewer> {
    this.preserveDrawingBufferEnabled =
      profile === "normal" && (config.contextOptions?.webgl?.preserveDrawingBuffer ?? false);
    // Éviter les réinitialisations multiples
    if (this.viewer) {
      console.warn("[CesiumManager] Viewer déjà initialisé");
      return this.viewer;
    }

    // Configurer le token Ion de Cesium depuis les variables d'environnement ou la config
    const ionToken = config.ionToken || import.meta.env.VITE_CESIUM_TOKEN;
    const hasIonToken = typeof ionToken === "string" && ionToken.length > 0;
    if (hasIonToken) {
      Cesium.Ion.defaultAccessToken = ionToken;
    } else {
      console.warn(
        "[CesiumManager] VITE_CESIUM_TOKEN absent: certaines ressources Cesium Ion peuvent être indisponibles",
      );
    }

    // OPTIMISATION: Configurer les décodeurs Draco pour la compression 3D
    // Draco peut réduire la taille des modèles GLB de 50-70%
    try {
      // @ts-ignore - DracoLoader existe mais n'est pas typé dans @types/cesium
      if (Cesium.DracoLoader) {
        // Cesium 1.138 gère Draco via ses Workers (ex: `Workers/decodeDraco.js`)
        // et les assets copiés par `vite-plugin-cesium`.
        // Garder ce bloc pour documenter l’intention, mais ne pas appeler d’API interne/obsolète.
      }
    } catch (error) {
      console.warn("[CesiumManager] Impossible de configurer Draco:", error);
      // Ne pas bloquer le chargement si Draco n'est pas disponible
    }

    // Rendre Cesium disponible globalement (requis par certains plugins)
    if (typeof window !== "undefined" && !(window as any).Cesium) {
      (window as any).Cesium = Cesium;
    }

    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(
        `[CesiumManager] Conteneur avec l'ID "${containerId}" non trouvé`,
      );
    }

    try {
      // Use Swiss topographic imagery

      const coucheFondSwissTopo = new Cesium.ImageryLayer(
        new Cesium.UrlTemplateImageryProvider({
          url: CesiumManager.SWISSTOPO_WMTS_TEMPLATE,
          // EPSG:3857 = Web Mercator, nativement supporte par Cesium
          tilingScheme: new Cesium.WebMercatorTilingScheme(),
          credit: "© swisstopo",
          minimumLevel: 0,
          maximumLevel: 19,
        }),
      );

      this.viewer = new Cesium.Viewer(containerId, {
        // === Désactivation complète des widgets UI ===
        animation: false,
        baseLayer: coucheFondSwissTopo,
        baseLayerPicker: false,
        fullscreenButton: false,
        geocoder: false,
        homeButton: false,
        infoBox: false,
        sceneModePicker: false,
        selectionIndicator: false,
        timeline: false,
        navigationHelpButton: false,

        // === Optimisations pour contenu statique (pas de temps/animation) ===
        clockViewModel: undefined, // Pas d'horloge nécessaire
        shouldAnimate: false, // Pas d'animation automatique
        automaticallyTrackDataSourceClocks: false, // Pas de synchronisation temporelle

        useBrowserRecommendedResolution:
          config.useBrowserRecommendedResolution ?? true,
        contextOptions: {
          ...config.contextOptions,
          requestWebgl1: profile === "webgl1",
          webgl: {
            ...config.contextOptions?.webgl,
            powerPreference: profile === "normal" ? "low-power" : "default",
            failIfMajorPerformanceCaveat: false,
            // Le buffer préservé a un coût GPU/mémoire non négligeable.
            // Il reste désactivé par défaut et n'est activé qu'en mode capture.
            preserveDrawingBuffer: this.preserveDrawingBufferEnabled,
            antialias: false, // Désactiver l'anti-aliasing WebGL natif
            depth: true,
            stencil: false, // Pas besoin de stencil buffer pour du statique
          },
        },
        scene3DOnly: true, // Désactiver les modes 2D/Columbus View
        requestRenderMode: true, // Rendu uniquement sur demande (critique pour statique)
        maximumRenderTimeChange: Infinity, // Jamais de rendu automatique basé sur le temps
        targetFrameRate: 45, // Limiter à 45 FPS max (suffisant pour navigation statique)
        msaaSamples: 1, // Désactiver MSAA
        // OIT: réduit les artefacts de tri quand des matériaux (ou textures) sont en alpha BLEND.
        // Par défaut on l'active pour éviter l'effet "bâtiments troués" en orbite autour du modèle.
        orderIndependentTranslucency:
          profile === "normal" && (config.enableOrderIndependentTranslucency ?? true),
        shadows: profile === "normal", // Limiter les besoins GPU en mode compatible
        terrainShadows: OMBRES_TERRAIN_PAR_DEFAUT, // Ombres receive only
        skyBox: false, // Désactiver la skybox (économie mémoire textures)
      });

      if (profile !== "normal") this.viewer.resolutionScale = 0.75;
      const controleCamera = this.viewer.scene.screenSpaceCameraController;
      // En mode 3D, translateEventTypes n'est pas pris en charge pour déplacer la vue.
      // On mappe donc le clic gauche sur la rotation/orbite afin de retrouver un pan visuel.
      controleCamera.translateEventTypes = [];
      controleCamera.rotateEventTypes = [
        Cesium.CameraEventType.LEFT_DRAG,
        Cesium.CameraEventType.RIGHT_DRAG,
      ];
      controleCamera.tiltEventTypes = [Cesium.CameraEventType.RIGHT_DRAG];
      controleCamera.lookEventTypes = [Cesium.CameraEventType.RIGHT_DRAG];
      controleCamera.zoomEventTypes = [Cesium.CameraEventType.WHEEL];

      // IMPORTANT: ne pas passer `skyAtmosphere: true` au constructeur.
      // Sur Cesium 1.138, cela peut remplacer `scene.skyAtmosphere` (objet) par un booléen,
      // ce qui fait planter le rendu (`setDynamicLighting` / `destroy` non trouvées).
      if (this.viewer.scene.skyAtmosphere) {
        this.viewer.scene.skyAtmosphere.show = true;
      }

      // === Désactivation complète du système de temps ===
      // Pour du contenu statique, on n'a pas besoin de l'horloge Cesium
      this.viewer.clock.shouldAnimate = false;
      this.viewer.clock.canAnimate = false;

      // Configurations de performance
      this.viewer.scene.globe.enableLighting = config.enableLighting ?? false; // Pas de lighting dynamique pour statique
      this.viewer.scene.globe.depthTestAgainstTerrain =
        config.enableDepthTesting ?? false; // Le tileset doit pouvoir s'afficher meme s'il est legerement sous le terrain

      // Activer l'éclairage de la scène pour les modèles 3D
      this.viewer.scene.light = new Cesium.SunLight({
        intensity: INTENSITE_LUMIERE_PAR_DEFAUT,
        color: COULEUR_LUMIERE_PAR_DEFAUT,
      });

      // Désactiver le HDR : le tone mapper Khronos Neutral PBR (défaut en Cesium 1.125+)
      // compresse fortement les valeurs et assombrit les modèles importés
      // En mode SDR, Cesium applique juste une correction gamma standard
      this.viewer.scene.highDynamicRange = false;
      this.viewer.scene.gamma = 1.0; // Pas de correction gamma supplémentaire en SDR

      // === Optimisations terrain ===
      this.viewer.scene.globe.tileCacheSize = 100; // Cache augmenté pour meilleure qualité
      this.viewer.scene.globe.maximumScreenSpaceError = 4; // Plus de détails du terrain
      this.viewer.scene.fog.enabled = false;
      this.viewer.scene.postProcessStages.fxaa.enabled = false; // Pas de FXAA

      // Éléments visuels : garder l'atmosphère et le soleil pour l'IBL
      if (this.viewer.scene.sun) {
        this.viewer.scene.sun.show = true;
      }
      if (this.viewer.scene.moon) {
        this.viewer.scene.moon.show = false;
      }
      if (this.viewer.scene.skyBox) {
        this.viewer.scene.skyBox.show = false;
      }

      // Désactiver le globe si on affiche uniquement des modèles 3D locaux
      // this.viewer.scene.globe.show = false;

      // Position initiale : utilise les valeurs du projet si définies, sinon les valeurs par défaut de la Suisse
      const initialLatitude =
        config.defaultLatitude ?? CesiumManager.DEFAULT_LATITUDE;
      const initialLongitude =
        config.defaultLongitude ?? CesiumManager.DEFAULT_LONGITUDE;
      const initialAltitude = config.defaultAltitude ?? 1000;
      const initialPitch =
        config.defaultPitch ??
        Cesium.Math.toRadians(CesiumManager.INITIAL_CAMERA_PITCH_DEGREES);

      // Sauvegarder le point central pour le culling du terrain si un point de départ est défini
      if (
        config.defaultLatitude !== undefined &&
        config.defaultLongitude !== undefined
      ) {
        this.terrainCenterPoint = Cesium.Cartesian3.fromDegrees(
          initialLongitude,
          initialLatitude,
          0, // Au niveau du sol pour le calcul de distance
        );
      }

      // Terrain swisstopo (geo.admin.ch) en quantized mesh
      try {
        const terrainProvider = await Cesium.CesiumTerrainProvider.fromUrl(
          CesiumManager.SWISSTOPO_TERRAIN_URL,
        );
        if (generation !== this.graphicsGeneration) throw new Error("Initialisation annulée");
        this.flattenTerrainProvider = new LocalizedFlattenTerrainProvider(
          terrainProvider,
        );
        this.viewer.scene.globe.terrainProvider = this.flattenTerrainProvider;

        // Configurer le globe pour optimiser la mémoire
        this.viewer.scene.globe.tileCacheSize = 100; // Limiter le cache de tuiles (défaut: 100)
        this.viewer.scene.globe.maximumScreenSpaceError = 4; // Réduire le niveau de détail (défaut: 2)

        // Si un point de départ est défini, activer le culling du terrain
        if (this.terrainCenterPoint) {
          this.setupTerrainCulling();
        }
      } catch (error) {
        if (generation !== this.graphicsGeneration) throw error;
        console.warn(
          "[CesiumManager] Impossible de charger le terrain swisstopo (geo.admin.ch), utilisation du terrain ellipsoïde",
          error,
        );
        this.flattenTerrainProvider = new LocalizedFlattenTerrainProvider(
          new Cesium.EllipsoidTerrainProvider(),
        );
        this.viewer.scene.globe.terrainProvider = this.flattenTerrainProvider;
      }

      this.applyInitialCameraView(
        initialLatitude,
        initialLongitude,
        initialAltitude,
        initialPitch,
      );

      return this.viewer;
    } catch (error) {
      console.error("[CesiumManager] Erreur lors de l'initialisation:", error);
      throw error;
    }
  }

  /**
   * Force un rendu de la scène
   * Nécessaire quand requestRenderMode est activé pour économiser les ressources
   */
  /**
   * Masque le relief du terrain (globe) dans la zone couverte par un tileset.
   * Calcule l'enveloppe convexe des tuiles enfants, puis la retrecit vers
   * l'interieur pour que le terrain swisstopo remplisse les bords naturellement.
   *
   * Si plusieurs tilesets sont chargés, les polygones de clipping sont combinés.
   */
  private buildCircularFootprintContours(
    center: Cesium.Cartesian3,
    radiusMeters: number,
    pointCount = 24,
  ): BasemapClipContour[] {
    const safeRadius = Math.max(radiusMeters, 2);
    const longitudesLatitudes: Array<[number, number]> = [];

    for (let index = 0; index < pointCount; index += 1) {
      const heading = (Math.PI * 2 * index) / pointCount;
      const localOffset = this.buildLocalOffsetWorldTranslation(center, {
        eastMeters: Math.cos(heading) * safeRadius,
        northMeters: Math.sin(heading) * safeRadius,
        upMeters: 0,
      });
      const perimeterPoint = Cesium.Cartesian3.add(
        center,
        localOffset,
        new Cesium.Cartesian3(),
      );
      const cartographic = Cesium.Cartographic.fromCartesian(perimeterPoint);
      longitudesLatitudes.push([
        Cesium.Math.toDegrees(cartographic.longitude),
        Cesium.Math.toDegrees(cartographic.latitude),
      ]);
    }

    return [
      {
        coordinates: longitudesLatitudes,
        inwardMarginMeters: 0,
        source: "tileset-json",
        version: 1,
      },
    ];
  }

  private computeTilesetClipContours(
    tileset: Cesium.Cesium3DTileset,
  ): BasemapClipContour[] {
    if (!this.viewer) return [];

    const root = tileset.root;
    if (!root?.children?.length) return [];

    const points: [number, number][] = [];
    let pointsFromHeaderRegion = 0;
    let pointsFromRuntimeRegion = 0;
    let pointsFromBoundingSphere = 0;
    for (const child of root.children) {
      const headerRegionPoints =
        this.extractClippingPointsFromTileHeaderRegion(child);
      if (headerRegionPoints.length > 0) {
        points.push(...headerRegionPoints);
        pointsFromHeaderRegion += headerRegionPoints.length;
        continue;
      }

      const regionPoints = this.extractClippingPointsFromTileRegion(child);
      if (regionPoints.length > 0) {
        points.push(...regionPoints);
        pointsFromRuntimeRegion += regionPoints.length;
        continue;
      }

      const boundingSpherePoints =
        this.extractClippingPointsFromTileBoundingSphere(child);
      points.push(...boundingSpherePoints);
      pointsFromBoundingSphere += boundingSpherePoints.length;
    }

    if (points.length < 3) {
      return [];
    }

    const hull = this.computeConvexHull(points);
    if (hull.length < 3) {
      return [];
    }

    let cLon = 0;
    let cLat = 0;
    for (const [lon, lat] of hull) {
      cLon += lon;
      cLat += lat;
    }
    cLon /= hull.length;
    cLat /= hull.length;

    const shrink = 0.42;
    const coordinates: Array<[number, number]> = [];
    for (const [lon, lat] of hull) {
      coordinates.push([
        Cesium.Math.toDegrees(cLon + (lon - cLon) * (1 - shrink)),
        Cesium.Math.toDegrees(cLat + (lat - cLat) * (1 - shrink)),
      ]);
    }

    return [
      {
        coordinates,
        inwardMarginMeters: 0,
        source: "tileset-json",
        version: 1,
      },
    ];
  }

  private clipGlobeTerrainUnderTileset(
    tileset: Cesium.Cesium3DTileset,
    modelId: string,
  ): void {
    const contours = this.computeTilesetClipContours(tileset);
    if (contours.length === 0) {
      this.removeModelTerrainFlatten(modelId);
      return;
    }

    this.setModelTerrainFlattenZone(modelId, contours);
  }

  private setGlobeTerrainClippingPolygons(
    modelId: string,
    polygons: Cesium.ClippingPolygon[],
  ): void {
    if (!this.viewer) return;

    if (polygons.length === 0) {
      this.globeClippingPolygons.delete(modelId);
    } else {
      this.globeClippingPolygons.set(modelId, polygons);
    }

    const allPolygons = Array.from(this.globeClippingPolygons.values()).flat();
    this.viewer.scene.globe.clippingPolygons =
      allPolygons.length === 0
        ? undefined!
        : new Cesium.ClippingPolygonCollection({
            polygons: allPolygons,
          });
    this.requestRender();
  }

  private applyPrecomputedGlobeTerrainClipping(
    contours: BasemapClipContour[],
    modelId: string,
  ): boolean {
    if (!this.viewer || contours.length === 0) {
      return false;
    }

    const polygons: Cesium.ClippingPolygon[] = [];

    for (const contour of contours) {
      if (
        !Array.isArray(contour.coordinates) ||
        contour.coordinates.length < 3
      ) {
        continue;
      }

      const radians: number[] = [];
      for (const [longitude, latitude] of contour.coordinates) {
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          continue;
        }
        radians.push(
          Cesium.Math.toRadians(longitude),
          Cesium.Math.toRadians(latitude),
        );
      }

      if (radians.length < 6) {
        continue;
      }

      polygons.push(
        new Cesium.ClippingPolygon({
          positions: Cesium.Cartesian3.fromRadiansArray(radians),
        }),
      );
    }

    if (polygons.length === 0) {
      return false;
    }

    this.setGlobeTerrainClippingPolygons(modelId, polygons);
    return true;
  }

  /**
   * Source la plus fiable pour un tileset transformé:
   * le header JSON brut, avant que Cesium ne convertisse une region en OBB.
   */
  private extractClippingPointsFromTileHeaderRegion(
    tile: Cesium.Cesium3DTile,
  ): [number, number][] {
    const header = (
      tile as unknown as {
        _header?: {
          boundingVolume?: {
            region?: [number, number, number, number, number, number];
          };
        };
      }
    )._header;

    const region = header?.boundingVolume?.region;
    if (!region || region.length < 4) {
      return [];
    }

    const [west, south, east, north] = region;
    return [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
    ];
  }

  /**
   * Privilégie la région WGS84 déclarée dans le tileset, qui reste fiable
   * même si Cesium expose un boundingSphere enfant dans un repère transformé.
   */
  private extractClippingPointsFromTileRegion(
    tile: Cesium.Cesium3DTile,
  ): [number, number][] {
    const boundingVolume = (
      tile as unknown as {
        boundingVolume?: {
          region?: {
            west: number;
            south: number;
            east: number;
            north: number;
          };
        };
      }
    ).boundingVolume;

    const region = boundingVolume?.region;
    if (!region) {
      return [];
    }

    return [
      [region.west, region.south],
      [region.east, region.south],
      [region.east, region.north],
      [region.west, region.north],
    ];
  }

  /**
   * Fallback historique pour les tuiles qui n'exposent pas de région.
   * On rejette les centres manifestement invalides pour éviter de découper
   * le terrain sur une zone sans rapport avec le projet.
   */
  private extractClippingPointsFromTileBoundingSphere(
    tile: Cesium.Cesium3DTile,
  ): [number, number][] {
    const bs = tile.boundingSphere;
    if (!bs) {
      return [];
    }

    let cartographic: Cesium.Cartographic;
    try {
      cartographic = Cesium.Cartographic.fromCartesian(bs.center);
    } catch {
      return [];
    }

    if (
      !Number.isFinite(cartographic.longitude) ||
      !Number.isFinite(cartographic.latitude)
    ) {
      return [];
    }

    const ar = bs.radius / Cesium.Ellipsoid.WGS84.maximumRadius;
    return [
      [cartographic.longitude - ar, cartographic.latitude],
      [cartographic.longitude + ar, cartographic.latitude],
      [cartographic.longitude, cartographic.latitude - ar],
      [cartographic.longitude, cartographic.latitude + ar],
    ];
  }

  /**
   * Enlève le clipping terrain associé à un tileset.
   * Utile lors du déchargement d'un tileset pour éviter les fuites mémoire.
   */
  removeGlobeTerrainClipping(modelId: string): void {
    if (!this.viewer) return;

    if (!this.globeClippingPolygons.has(modelId)) return;

    this.setGlobeTerrainClippingPolygons(modelId, []);
  }

  /**
   * Synchronise le clipping terrain pour un tileset déjà chargé selon sa visibilité.
   * Cela évite de laisser un "trou" dans le globe quand le basemap est simplement masqué.
   */
  syncGlobeTerrainClipping(
    modelId: string,
    primitive: Cesium.Cesium3DTileset | Cesium.Model,
    visible: boolean,
  ): void {
    if (primitive instanceof Cesium.Cesium3DTileset) {
      if (visible) {
        this.clipGlobeTerrainUnderTileset(primitive, modelId);
      } else {
        this.removeGlobeTerrainClipping(modelId);
      }
    }
  }

  private resolveModelBaseAnchorPosition(
    primitive: Cesium.Cesium3DTileset | Cesium.Model,
    baseModelMatrix: Cesium.Matrix4,
  ): Cesium.Cartesian3 | undefined {
    const boundingSphereCenter = primitive.boundingSphere?.center;
    if (boundingSphereCenter) {
      return Cesium.Cartesian3.clone(
        boundingSphereCenter,
        new Cesium.Cartesian3(),
      );
    }

    const matrixTranslation = Cesium.Matrix4.getTranslation(
      baseModelMatrix,
      new Cesium.Cartesian3(),
    );

    if (Cesium.Cartesian3.magnitude(matrixTranslation) > 0) {
      return matrixTranslation;
    }

    return undefined;
  }

  private buildLocalOffsetWorldTranslation(
    baseAnchorPosition: Cesium.Cartesian3,
    offset: {
      eastMeters: number;
      northMeters: number;
      upMeters: number;
    },
  ): Cesium.Cartesian3 {
    const enuFrame =
      Cesium.Transforms.eastNorthUpToFixedFrame(baseAnchorPosition);

    const eastAxis = Cesium.Cartesian3.normalize(
      new Cesium.Cartesian3(enuFrame[0], enuFrame[1], enuFrame[2]),
      new Cesium.Cartesian3(),
    );
    const northAxis = Cesium.Cartesian3.normalize(
      new Cesium.Cartesian3(enuFrame[4], enuFrame[5], enuFrame[6]),
      new Cesium.Cartesian3(),
    );
    const upAxis = Cesium.Cartesian3.normalize(
      new Cesium.Cartesian3(enuFrame[8], enuFrame[9], enuFrame[10]),
      new Cesium.Cartesian3(),
    );

    const translationOffset = new Cesium.Cartesian3(0, 0, 0);
    Cesium.Cartesian3.add(
      translationOffset,
      Cesium.Cartesian3.multiplyByScalar(
        eastAxis,
        offset.eastMeters,
        new Cesium.Cartesian3(),
      ),
      translationOffset,
    );
    Cesium.Cartesian3.add(
      translationOffset,
      Cesium.Cartesian3.multiplyByScalar(
        northAxis,
        offset.northMeters,
        new Cesium.Cartesian3(),
      ),
      translationOffset,
    );
    Cesium.Cartesian3.add(
      translationOffset,
      Cesium.Cartesian3.multiplyByScalar(
        upAxis,
        offset.upMeters,
        new Cesium.Cartesian3(),
      ),
      translationOffset,
    );

    return translationOffset;
  }

  private buildAlignedModelMatrix(
    baseModelMatrix: Cesium.Matrix4,
    baseAnchorPosition: Cesium.Cartesian3,
    alignment: LocalModelAlignment,
  ): Cesium.Matrix4 {
    const worldTranslation = this.buildLocalOffsetWorldTranslation(
      baseAnchorPosition,
      alignment,
    );
    const translationMatrix = Cesium.Matrix4.fromTranslation(
      worldTranslation,
      new Cesium.Matrix4(),
    );

    const heading = Cesium.Math.toRadians(alignment.headingDegrees ?? 0);
    const pitch = Cesium.Math.toRadians(alignment.pitchDegrees ?? 0);
    const roll = Cesium.Math.toRadians(alignment.rollDegrees ?? 0);
    const rotationQuaternion = Cesium.Quaternion.fromHeadingPitchRoll(
      new Cesium.HeadingPitchRoll(heading, pitch, roll),
    );
    const rotationMatrix3 = Cesium.Matrix3.fromQuaternion(rotationQuaternion);
    const rotationMatrix = Cesium.Matrix4.fromRotationTranslation(
      rotationMatrix3,
      Cesium.Cartesian3.ZERO,
      new Cesium.Matrix4(),
    );

    const enuToFixedFrame =
      Cesium.Transforms.eastNorthUpToFixedFrame(baseAnchorPosition);
    const fixedFrameToEnu = Cesium.Matrix4.inverseTransformation(
      enuToFixedFrame,
      new Cesium.Matrix4(),
    );
    const anchoredRotation = Cesium.Matrix4.multiply(
      enuToFixedFrame,
      rotationMatrix,
      new Cesium.Matrix4(),
    );
    Cesium.Matrix4.multiply(
      anchoredRotation,
      fixedFrameToEnu,
      anchoredRotation,
    );

    const rotatedMatrix = Cesium.Matrix4.multiply(
      anchoredRotation,
      baseModelMatrix,
      new Cesium.Matrix4(),
    );

    return Cesium.Matrix4.multiply(
      translationMatrix,
      rotatedMatrix,
      new Cesium.Matrix4(),
    );
  }

  private translateClipContours(
    contours: BasemapClipContour[],
    baseAnchorPosition: Cesium.Cartesian3,
    offset: {
      eastMeters: number;
      northMeters: number;
      upMeters: number;
    },
  ): BasemapClipContour[] {
    if (
      contours.length === 0 ||
      (!offset.eastMeters && !offset.northMeters && !offset.upMeters)
    ) {
      return contours;
    }

    const worldTranslation = this.buildLocalOffsetWorldTranslation(
      baseAnchorPosition,
      offset,
    );

    return contours.map((contour) => ({
      ...contour,
      coordinates: contour.coordinates
        .map(([longitude, latitude]) => {
          const sourcePosition = Cesium.Cartesian3.fromDegrees(
            longitude,
            latitude,
            0,
          );
          const translatedPosition = Cesium.Cartesian3.add(
            sourcePosition,
            worldTranslation,
            new Cesium.Cartesian3(),
          );
          const cartographic =
            Cesium.Cartographic.fromCartesian(translatedPosition);

          return [
            Cesium.Math.toDegrees(cartographic.longitude),
            Cesium.Math.toDegrees(cartographic.latitude),
          ] as [number, number];
        })
        .filter(
          (coordinate): coordinate is [number, number] =>
            Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1]),
        ),
    }));
  }

  private resolveTranslatedTerrainContours(
    id: string,
    primitive: Cesium.Cesium3DTileset | Cesium.Model,
    contours: BasemapClipContour[],
    offset: {
      eastMeters: number;
      northMeters: number;
      upMeters: number;
    },
  ): BasemapClipContour[] {
    const model = this.loadedModels.get(id);
    if (!model) {
      return contours;
    }

    const metadata = (model.metadata ??= {
      kind:
        model.primitive instanceof Cesium.Cesium3DTileset ? "tileset" : "model",
      position: null,
      headingDeg: 0,
      pitchDeg: 0,
      rollDeg: 0,
      scale: 1,
    });
    const baseModelMatrix =
      (metadata.baseModelMatrix as Cesium.Matrix4 | undefined) ??
      Cesium.Matrix4.clone(primitive.modelMatrix, new Cesium.Matrix4());
    metadata.baseModelMatrix = baseModelMatrix;

    const baseAnchorPosition =
      (metadata.baseAnchorPosition as Cesium.Cartesian3 | undefined) ??
      this.resolveModelBaseAnchorPosition(primitive, baseModelMatrix);

    if (!baseAnchorPosition) {
      console.warn(
        `[CesiumManager] Impossible de déterminer le point d'ancrage du modèle "${id}" pour mettre à jour le terrain local`,
      );
      return contours;
    }

    metadata.baseAnchorPosition = baseAnchorPosition;
    return this.translateClipContours(contours, baseAnchorPosition, {
      eastMeters: offset.eastMeters,
      northMeters: offset.northMeters,
      upMeters: 0,
    });
  }

  syncModelTerrainFlatten(
    id: string,
    offset: {
      eastMeters: number;
      northMeters: number;
      upMeters: number;
    } = { eastMeters: 0, northMeters: 0, upMeters: 0 },
  ): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    if (!model.visible || !model.terrainFlattenEnabled) {
      this.removeModelTerrainFlatten(id);
      return;
    }

    let contours = model.terrainFlattenContours ?? [];
    if (contours.length > 0) {
      contours = this.resolveTranslatedTerrainContours(
        id,
        model.primitive,
        contours,
        offset,
      );
      model.terrainFlattenContours = contours;
      this.setModelTerrainFlattenZone(id, contours);
      return;
    }

    if (model.primitive instanceof Cesium.Model) {
      const boundingSphere = model.primitive.boundingSphere;
      if (boundingSphere?.center && Number.isFinite(boundingSphere.radius)) {
        const computedContours = this.buildCircularFootprintContours(
          boundingSphere.center,
          Math.max(boundingSphere.radius, 6),
        );
        model.terrainFlattenContours = computedContours;
        this.setModelTerrainFlattenZone(id, computedContours);
        return;
      }
    }

    if (model.primitive instanceof Cesium.Cesium3DTileset) {
      // Pour les basemaps 3D Tiles, on évite désormais de créer une zone de flatten
      // par défaut afin de réserver l'aplatissement aux modèles équipes.
      this.removeModelTerrainFlatten(id);
      return;
    }

    this.removeModelTerrainFlatten(id);
  }

  setModelTerrainClipping(
    id: string,
    enabled: boolean,
    contours: BasemapClipContour[] = [],
    offset: {
      eastMeters: number;
      northMeters: number;
      upMeters: number;
    } = { eastMeters: 0, northMeters: 0, upMeters: 0 },
  ): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    if (model.primitive instanceof Cesium.Cesium3DTileset) {
      // Les basemaps 3D Tiles conservent l'ancien comportement de clipping dédié.
      // Le flatten local doit désormais viser uniquement les modèles équipes.
      this.removeModelTerrainFlatten(id);

      if (!enabled) {
        this.removeGlobeTerrainClipping(id);
        return;
      }

      const translatedContours = this.resolveTranslatedTerrainContours(
        id,
        model.primitive,
        contours,
        offset,
      );
      const appliedPrecomputedClipping =
        this.applyPrecomputedGlobeTerrainClipping(translatedContours, id);

      if (!appliedPrecomputedClipping) {
        this.removeGlobeTerrainClipping(id);
      }
      return;
    }

    model.terrainFlattenEnabled = enabled;
    model.terrainFlattenContours = contours;

    if (!enabled) {
      this.removeModelTerrainFlatten(id);
      return;
    }

    this.syncModelTerrainFlatten(id, offset);
  }

  /**
   * Enveloppe convexe 2D (Andrew's monotone chain).
   * Entree: tableau de [lon, lat] en radians.
   * Sortie: polygone convexe en ordre anti-horaire.
   */
  private computeConvexHull(points: [number, number][]): [number, number][] {
    const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const n = sorted.length;
    if (n <= 2) return sorted;

    const cross = (
      o: [number, number],
      a: [number, number],
      b: [number, number],
    ) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

    // Partie basse
    const lower: [number, number][] = [];
    for (const p of sorted) {
      while (
        lower.length >= 2 &&
        cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
      ) {
        lower.pop();
      }
      lower.push(p);
    }
    // Partie haute
    const upper: [number, number][] = [];
    for (let i = n - 1; i >= 0; i--) {
      const p = sorted[i];
      while (
        upper.length >= 2 &&
        cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
      ) {
        upper.pop();
      }
      upper.push(p);
    }
    // Retirer le dernier point de chaque moitie (duplique)
    lower.pop();
    upper.pop();
    return lower.concat(upper);
  }

  requestRender(): void {
    if (this.viewer && !this.viewer.isDestroyed()) {
      this.viewer.scene.requestRender();
    }
  }

  setSwissTopoBackgroundVisibility(visible: boolean): void {
    if (!this.viewer || this.viewer.isDestroyed()) {
      return;
    }

    this.viewer.scene.globe.show = visible;
    this.requestRender();
  }

  isPreserveDrawingBufferEnabled(): boolean {
    return this.preserveDrawingBufferEnabled;
  }

  private computeFlattenHeightFromContours(
    contours: BasemapClipContour[],
  ): number | undefined {
    if (!this.viewer || contours.length === 0) {
      return undefined;
    }

    const points = contours.flatMap((contour) =>
      contour.coordinates.filter(
        (coordinate) =>
          Array.isArray(coordinate) &&
          coordinate.length >= 2 &&
          Number.isFinite(coordinate[0]) &&
          Number.isFinite(coordinate[1]),
      ),
    );

    if (points.length === 0) {
      return undefined;
    }

    let longitudeSum = 0;
    let latitudeSum = 0;
    for (const [longitude, latitude] of points) {
      longitudeSum += longitude;
      latitudeSum += latitude;
    }

    const centerLongitude = longitudeSum / points.length;
    const centerLatitude = latitudeSum / points.length;
    const cartographic = Cesium.Cartographic.fromDegrees(
      centerLongitude,
      centerLatitude,
    );

    return this.viewer.scene.globe.getHeight(cartographic) ?? 0;
  }

  private clearFlattenTerrainDebugEntities(modelId?: string): void {
    if (!this.viewer || this.viewer.isDestroyed()) {
      return;
    }

    if (modelId) {
      const entities = this.flattenTerrainDebugEntities.get(modelId) ?? [];
      for (const entity of entities) {
        this.viewer.entities.remove(entity);
      }
      this.flattenTerrainDebugEntities.delete(modelId);
      return;
    }

    for (const entities of this.flattenTerrainDebugEntities.values()) {
      for (const entity of entities) {
        this.viewer.entities.remove(entity);
      }
    }
    this.flattenTerrainDebugEntities.clear();
  }

  private syncFlattenTerrainDebugEntities(): void {
    if (!this.viewer || this.viewer.isDestroyed()) {
      return;
    }

    this.clearFlattenTerrainDebugEntities();

    if (!this.flattenTerrainDebugEnabled) {
      return;
    }

    if (this.terrainFlattenZones.size === 0) {
      console.warn(
        "[CesiumManager] Debug flatten terrain activé, mais aucune zone n'est enregistrée.",
      );
      return;
    }

    for (const [modelId, zone] of this.terrainFlattenZones.entries()) {
      const entities: Cesium.Entity[] = [];

      for (const contour of zone.contours) {
        if (
          !Array.isArray(contour.coordinates) ||
          contour.coordinates.length < 3
        ) {
          continue;
        }

        const hierarchyPositions = contour.coordinates.flatMap(
          ([longitude, latitude]) => [longitude, latitude],
        );
        const closedPositions = [
          ...hierarchyPositions,
          ...hierarchyPositions.slice(0, 2),
        ];

        let centreLongitude = 0;
        let centreLatitude = 0;
        for (const [longitude, latitude] of contour.coordinates) {
          centreLongitude += longitude;
          centreLatitude += latitude;
        }
        centreLongitude /= contour.coordinates.length;
        centreLatitude /= contour.coordinates.length;

        const polygonEntity = this.viewer.entities.add({
          id: `flatten-debug-polygon-${modelId}-${entities.length}`,
          polygon: {
            hierarchy: Cesium.Cartesian3.fromDegreesArray(hierarchyPositions),
            material: Cesium.Color.YELLOW.withAlpha(0.25),
            outline: true,
            outlineColor: Cesium.Color.YELLOW,
            height: zone.hauteurMetres + 0.2,
            extrudedHeight: zone.hauteurMetres + 0.25,
            perPositionHeight: false,
          },
        });
        entities.push(polygonEntity);

        const outlineEntity = this.viewer.entities.add({
          id: `flatten-debug-outline-${modelId}-${entities.length}`,
          polyline: {
            positions: Cesium.Cartesian3.fromDegreesArray(closedPositions),
            clampToGround: true,
            width: 6,
            material: Cesium.Color.YELLOW,
          },
        });
        entities.push(outlineEntity);

        const centerEntity = this.viewer.entities.add({
          id: `flatten-debug-center-${modelId}-${entities.length}`,
          position: Cesium.Cartesian3.fromDegrees(
            centreLongitude,
            centreLatitude,
            zone.hauteurMetres + 1.5,
          ),
          point: {
            pixelSize: 10,
            color: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 2,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
          label: {
            text: `flatten ${modelId}`,
            font: "12px sans-serif",
            fillColor: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 3,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            pixelOffset: new Cesium.Cartesian2(0, -18),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        entities.push(centerEntity);
      }

      if (entities.length > 0) {
        this.flattenTerrainDebugEntities.set(modelId, entities);
      }
    }
  }

  setFlattenTerrainDebugEnabled(enabled: boolean): void {
    this.flattenTerrainDebugEnabled = enabled;
    this.syncFlattenTerrainDebugEntities();
    this.requestRender();
  }

  private refreshTerrainFlattenZones(): void {
    if (!this.flattenTerrainProvider) {
      return;
    }

    this.flattenTerrainProvider.setFlattenZones(
      Array.from(this.terrainFlattenZones.values()),
    );
    this.syncFlattenTerrainDebugEntities();

    if (this.viewer && !this.viewer.isDestroyed()) {
      this.viewer.scene.globe.terrainProvider = this.flattenTerrainProvider;
      this.requestRender();
    }
  }

  private setModelTerrainFlattenZone(
    modelId: string,
    contours: BasemapClipContour[],
    heightMeters?: number,
  ): void {
    if (!this.flattenTerrainProvider) {
      return;
    }

    if (contours.length === 0) {
      this.terrainFlattenZones.delete(modelId);
      this.refreshTerrainFlattenZones();
      this.syncFlattenTerrainDebugEntities();
      return;
    }

    const flattenHeightCandidate =
      heightMeters ?? this.computeFlattenHeightFromContours(contours);

    if (
      flattenHeightCandidate === undefined ||
      !Number.isFinite(flattenHeightCandidate)
    ) {
      return;
    }

    const flattenHeight = flattenHeightCandidate;

    this.terrainFlattenZones.set(modelId, {
      id: modelId,
      contours,
      hauteurMetres: flattenHeight,
    });

    this.refreshTerrainFlattenZones();
    this.syncFlattenTerrainDebugEntities();
  }

  removeModelTerrainFlatten(modelId: string): void {
    if (!this.terrainFlattenZones.has(modelId)) {
      return;
    }

    this.terrainFlattenZones.delete(modelId);
    this.refreshTerrainFlattenZones();
    this.syncFlattenTerrainDebugEntities();
  }

  /**
   * Détruit le viewer et nettoie les ressources
   */
  destroy(): void {
    // Décharger tous les modèles d'abord
    this.loadedModels.forEach((model) => {
      try {
        if (model.primitive && !model.primitive.isDestroyed?.()) {
          // primitives.remove() retire et détruit la primitive par défaut
          if (this.viewer && !this.viewer.isDestroyed()) {
            this.viewer.scene.primitives.remove(model.primitive);
          }
        }
      } catch (error) {
        console.warn(
          "[CesiumManager] Erreur lors de la destruction du modèle:",
          error,
        );
      }
    });
    this.loadedModels.clear();
    this.terrainFlattenZones.clear();
    this.globeClippingPolygons.clear();
    this.clearFlattenTerrainDebugEntities();
    this.flattenTerrainProvider = null;
    this.terrainCenterPoint = null;

    // Annuler toutes les animations en cours
    this.runningModelArrivalAnimations.forEach((cancel) => {
      try {
        cancel();
      } catch {
        // Ignorer les erreurs de cancellation
      }
    });
    this.runningModelArrivalAnimations.clear();

    // Détruire le viewer s'il existe et n'est pas déjà détruit
    if (this.viewer) {
      try {
        if (!this.viewer.isDestroyed()) {
          this.viewer.destroy();
        }
      } catch (error) {
        console.warn(
          "[CesiumManager] Erreur lors de la destruction du viewer:",
          error,
        );
      }
      // Toujours mettre à null pour permettre la réinitialisation
      this.viewer = null;
    }
  }

  /**
   * Configure le culling du terrain pour limiter le chargement
   * à une zone autour du point de départ
   */
  private setupTerrainCulling(): void {
    if (!this.viewer || !this.terrainCenterPoint) {
      return;
    }

    const globe = this.viewer.scene.globe;
    const loadRadius = CesiumManager.TERRAIN_LOAD_RADIUS;

    // Calculer un rectangle approximatif autour du point de départ
    // Convertir la distance en degrés approximativement (1 degré ≈ 111km à cette latitude)
    const radiusInDegrees = loadRadius / 111000; // Conversion approximative mètres → degrés

    // Obtenir la position en coordonnées cartographiques
    const centerCarto = Cesium.Cartographic.fromCartesian(
      this.terrainCenterPoint,
    );

    // Créer un rectangle avec cette zone
    const limitRectangle = new Cesium.Rectangle(
      centerCarto.longitude - radiusInDegrees,
      centerCarto.latitude - radiusInDegrees,
      centerCarto.longitude + radiusInDegrees,
      centerCarto.latitude + radiusInDegrees,
    );

    // Appliquer la limitation de chargement
    // Note: cartographicLimitRectangle limite la zone de rendu mais pas le chargement
    // On doit donc combiner avec d'autres optimisations
    globe.cartographicLimitRectangle = limitRectangle;

    // Réduire le pré-chargement des tuiles voisines et parentes
    globe.preloadAncestors = false; // Ne pas précharger les tuiles parentes
    globe.preloadSiblings = false; // Ne pas précharger les tuiles voisines
  }

  /**
   * Vérifie si le viewer est initialisé et valide
   */
  isInitialized(): boolean {
    return !this.initialization && this.viewer !== null && !this.viewer.isDestroyed();
  }

  /**
   * Réinitialise le singleton (utile pour les tests et le logout)
   */
  reset(): void {
    this.graphicsGeneration += 1;
    this.initialization = null;
    this.destroy();
  }

  /**
   * Récupère le viewer Cesium
   */
  getViewer(): Cesium.Viewer {
    if (this.initialization || !this.viewer || this.viewer.isDestroyed()) {
      throw new Error(
        "[CesiumManager] Viewer n'est pas initialisé. Appelez initialize() d'abord.",
      );
    }
    return this.viewer;
  }

  /**
   * Attend que le modèle soit prêt ou échoue explicitement.
   * Évite les promesses pendantes en cas d'erreur Draco/WASM dans les workers Cesium.
   */
  private async waitForModelReadyOrError(
    model: Cesium.Model,
    modelId: string,
  ): Promise<void> {
    if (model.ready) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const cleanups: Array<() => void> = [];

      const cleanup = () => {
        while (cleanups.length > 0) {
          const fn = cleanups.pop();
          try {
            fn?.();
          } catch {
            // Nettoyage défensif: ignorer les erreurs de callback.
          }
        }
      };

      const resolveOnce = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      const rejectOnce = (reason: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (reason instanceof Error) {
          reject(reason);
          return;
        }
        reject(new Error(String(reason)));
      };

      const resolveIfReady = () => {
        if (settled) {
          return true;
        }

        if (model.isDestroyed?.()) {
          rejectOnce(
            new Error(
              `[CesiumManager] Modèle "${modelId}" détruit avant la fin du chargement`,
            ),
          );
          return true;
        }

        if (model.ready) {
          resolveOnce();
          return true;
        }

        return false;
      };

      if (resolveIfReady()) {
        return;
      }

      const anyModel = model as unknown as {
        errorEvent?: {
          addEventListener?: (cb: (error: unknown) => void) => () => void;
        };
      };

      if (anyModel.errorEvent?.addEventListener) {
        const removeError = anyModel.errorEvent.addEventListener((error) => {
          rejectOnce(
            error instanceof Error
              ? error
              : new Error(
                  `[CesiumManager] Erreur de chargement du modèle "${modelId}"`,
                ),
          );
        });
        cleanups.push(removeError);
      }

      const removeReady = model.readyEvent.addEventListener(() => {
        resolveOnce();
      });
      cleanups.push(removeReady);

      // `readyEvent` peut être émis juste après `fromGltfAsync`, avant que le
      // callback soit branché. On revalide immédiatement puis on sonde en
      // secours pour éviter les faux timeouts intermittents.
      if (resolveIfReady()) {
        return;
      }

      const readyPollIntervalId = globalThis.setInterval(() => {
        if (this.viewer && !this.viewer.isDestroyed()) {
          this.viewer.scene.requestRender();
        }
        resolveIfReady();
      }, 100);
      cleanups.push(() => globalThis.clearInterval(readyPollIntervalId));

      const modelReadyTimeoutMs = CesiumManager.getModelReadyTimeoutMs();
      const timeoutId = globalThis.setTimeout(() => {
        rejectOnce(
          new Error(
            `[CesiumManager] Timeout de chargement du modèle "${modelId}" (${modelReadyTimeoutMs}ms)`,
          ),
        );
      }, modelReadyTimeoutMs);
      cleanups.push(() => globalThis.clearTimeout(timeoutId));
    });
  }

  /**
   * Attend la fin du chargement initial d'un tileset 3D.
   * Le chargement complet reste dynamique, mais cette attente couvre
   * la phase où les premières tuiles nécessaires à l'affichage arrivent.
   */
  private async waitForTilesetInitialStreaming(
    tileset: Cesium.Cesium3DTileset,
    modelId: string,
    onProgress?: (
      progress: number,
      determinate: boolean,
      pendingRequests?: number,
      pendingRequestsPeak?: number,
    ) => void,
  ): Promise<void> {
    const anyTileset = tileset as unknown as {
      tileLoadProgressEvent?: {
        addEventListener?: (
          cb: (pendingRequests: number) => void,
        ) => () => void;
      };
      initialTilesLoaded?: {
        addEventListener?: (cb: () => void) => () => void;
      };
      allTilesLoaded?: { addEventListener?: (cb: () => void) => () => void };
    };

    const report = (
      progress: number,
      determinate: boolean,
      pendingRequests?: number,
      pendingRequestsPeak?: number,
    ) => {
      onProgress?.(
        Math.max(0, Math.min(1, progress)),
        determinate,
        pendingRequests,
        pendingRequestsPeak,
      );
    };

    await new Promise<void>((resolve) => {
      let settled = false;
      let pendingRequestsPeak = 0;
      let sawPendingRequests = false;
      let lastProgress = 0.12;
      const cleanups: Array<() => void> = [];

      const cleanup = () => {
        while (cleanups.length > 0) {
          const fn = cleanups.pop();
          try {
            fn?.();
          } catch {
            // Nettoyage défensif.
          }
        }
      };

      const resolveOnce = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      const addEventCleanup = (remover: (() => void) | void) => {
        if (typeof remover === "function") {
          cleanups.push(remover);
        }
      };

      report(lastProgress, false);

      if (anyTileset.tileLoadProgressEvent?.addEventListener) {
        const removeTileProgress =
          anyTileset.tileLoadProgressEvent.addEventListener(
            (pendingRequests) => {
              if (!Number.isFinite(pendingRequests)) {
                return;
              }

              if (pendingRequests > 0) {
                sawPendingRequests = true;
                pendingRequestsPeak = Math.max(
                  pendingRequestsPeak,
                  pendingRequests,
                );
              }

              if (pendingRequestsPeak > 0) {
                const ratio = Math.max(
                  0,
                  Math.min(
                    1,
                    (pendingRequestsPeak - pendingRequests) /
                      pendingRequestsPeak,
                  ),
                );
                const nextProgress = Math.max(
                  lastProgress,
                  0.12 + ratio * 0.83,
                );
                lastProgress = Math.min(nextProgress, 0.95);
                report(
                  lastProgress,
                  true,
                  pendingRequests,
                  pendingRequestsPeak,
                );
              } else {
                report(lastProgress, false, pendingRequests);
              }

              if (sawPendingRequests && pendingRequests === 0) {
                resolveOnce();
              }
            },
          );
        addEventCleanup(removeTileProgress);
      }

      if (anyTileset.initialTilesLoaded?.addEventListener) {
        const removeInitialTilesLoaded =
          anyTileset.initialTilesLoaded.addEventListener(() => {
            report(0.96, true, 0, pendingRequestsPeak || undefined);
            resolveOnce();
          });
        addEventCleanup(removeInitialTilesLoaded);
      }

      if (anyTileset.allTilesLoaded?.addEventListener) {
        const removeAllTilesLoaded = anyTileset.allTilesLoaded.addEventListener(
          () => {
            report(0.98, true, 0, pendingRequestsPeak || undefined);
            resolveOnce();
          },
        );
        addEventCleanup(removeAllTilesLoaded);
      }

      // Filet de sécurité: on ne bloque pas la suite si Cesium ne fournit pas d'événement exploitable.
      const tilesetStreamTimeoutMs = CesiumManager.getTilesetStreamTimeoutMs();
      const timeoutId = globalThis.setTimeout(() => {
        console.warn(
          `[CesiumManager] Timeout suivi tileset "${modelId}" (${tilesetStreamTimeoutMs}ms), poursuite sans blocage`,
        );
        resolveOnce();
      }, tilesetStreamTimeoutMs);
      cleanups.push(() => globalThis.clearTimeout(timeoutId));
    });
  }

  /**
   * Charge un modèle 3D (Cesium3DTileset ou Model)
   */
  async loadModel(id: string, options: ModelLoadOptions): Promise<void> {
    if (!this.viewer) {
      throw new Error("[CesiumManager] Viewer n'est pas initialisé");
    }

    // Vérifier si le modèle est déjà chargé
    if (this.loadedModels.has(id)) {
      const existing = this.loadedModels.get(id)!;
      // Nettoyer les entrées fantômes (primitive déjà détruite)
      if (existing.primitive?.isDestroyed?.()) {
        console.warn(`[CesiumManager] Nettoyage entrée fantôme pour "${id}"`);
        this.loadedModels.delete(id);
      } else {
        console.warn(`[CesiumManager] Modèle "${id}" déjà chargé`);
        return;
      }
    }

    let attempt = 0;
    let lastError: unknown = null;

    while (attempt <= CesiumManager.TRANSIENT_LOAD_RETRY_COUNT) {
      try {
        await this.loadModelInternal(id, options);
        return;
      } catch (error) {
        lastError = error;

        const shouldRetry =
          attempt < CesiumManager.TRANSIENT_LOAD_RETRY_COUNT &&
          this.isTransientLoadError(error);

        if (!shouldRetry) {
          throw error;
        }

        console.warn(
          `[CesiumManager] Retry chargement "${id}" après erreur transitoire (tentative ${attempt + 1}/${CesiumManager.TRANSIENT_LOAD_RETRY_COUNT + 1})`,
          error,
        );
        this.unloadModel(id);
      } finally {
        attempt += 1;
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error(`[CesiumManager] Échec du chargement du modèle "${id}"`);
  }

  /**
   * Décharge un modèle
   */
  unloadModel(id: string): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    const stopArrivalAnimation = this.runningModelArrivalAnimations.get(id);
    if (stopArrivalAnimation) {
      stopArrivalAnimation();
      this.runningModelArrivalAnimations.delete(id);
    }

    // Toujours supprimer de la map en premier, même si la destruction échoue ensuite.
    // Empêche les entrées fantômes avec des primitives détruites.
    this.loadedModels.delete(id);

    try {
      if (model.primitive && !model.primitive.isDestroyed?.()) {
        // primitives.remove() retire la primitive de la scène ET la détruit par défaut
        // (PrimitiveCollection._destroyPrimitives = true).
        // Pas besoin d'appeler destroy() explicitement, ce qui provoquerait
        // une DeveloperError car les méthodes sont déjà remplacées.
        if (this.viewer && !this.viewer.isDestroyed()) {
          this.viewer.scene.primitives.remove(model.primitive);
        }
      }
      this.removeModelTerrainFlatten(id);
    } catch (error) {
      console.error(
        `[CesiumManager] Erreur lors de la destruction du modèle "${id}":`,
        error,
      );
    }
  }

  /**
   * Change la visibilité d'un modèle
   */
  setModelVisibility(id: string, visible: boolean): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    try {
      // Vérifier que la primitive n'est pas détruite
      if (model.primitive.isDestroyed?.()) {
        console.warn(`[CesiumManager] Primitive "${id}" déjà détruite`);
        return;
      }

      model.visible = visible;

      // Cesium3DTileset et Model ont des propriétés différentes
      if (model.primitive instanceof Cesium.Cesium3DTileset) {
        model.primitive.show = visible;
      } else if (model.primitive instanceof Cesium.Model) {
        model.primitive.show = visible;
      }

      if (model.terrainFlattenEnabled) {
        if (visible) {
          this.syncModelTerrainFlatten(id);
        } else {
          this.removeModelTerrainFlatten(id);
        }
      }

      this.requestRender();
    } catch (error) {
      console.error(
        `[CesiumManager] Erreur lors du changement de visibilité:`,
        error,
      );
    }
  }

  /**
   * Change l'opacité d'un modèle
   */
  setModelOpacity(id: string, opacity: number): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    try {
      // Vérifier que la primitive n'est pas détruite
      if (model.primitive.isDestroyed?.()) {
        console.warn(`[CesiumManager] Primitive "${id}" déjà détruite`);
        return;
      }

      // Limiter l'opacité entre 0 et 1
      const clampedOpacity = Math.max(0, Math.min(1, opacity));
      model.opacity = clampedOpacity;

      if (model.primitive instanceof Cesium.Model) {
        // Préserver intégralement les matériaux/textures du GLTF/GLB.
        // On ne pilote ici que l'alpha global du modèle.
        if (!model.primitive.color) {
          model.primitive.color = Cesium.Color.WHITE.clone();
        }
        model.primitive.color = Cesium.Color.WHITE.withAlpha(clampedOpacity);
        model.primitive.colorBlendMode = Cesium.ColorBlendMode.HIGHLIGHT;
        model.primitive.colorBlendAmount = 0.0;
      } else if (model.primitive instanceof Cesium.Cesium3DTileset) {
        // Pour les 3D Tilesets - utiliser rgba pour préserver les couleurs d'origine
        model.primitive.style = new Cesium.Cesium3DTileStyle({
          color: `rgba(255, 255, 255, ${clampedOpacity})`,
        });
      }
      this.requestRender();
    } catch (error) {
      console.error(
        `[CesiumManager] Erreur lors du changement d'opacité:`,
        error,
      );
    }
  }

  setModelLocalOffset(id: string, offset: LocalModelAlignment): void {
    const model = this.loadedModels.get(id);
    if (!model) {
      console.warn(`[CesiumManager] Modèle "${id}" non trouvé`);
      return;
    }

    try {
      if (model.primitive.isDestroyed?.()) {
        console.warn(`[CesiumManager] Primitive "${id}" déjà détruite`);
        return;
      }

      if (
        !(model.primitive instanceof Cesium.Cesium3DTileset) &&
        !(model.primitive instanceof Cesium.Model)
      ) {
        return;
      }

      const metadata = (model.metadata ??= {
        kind:
          model.primitive instanceof Cesium.Cesium3DTileset
            ? "tileset"
            : "model",
        position: null,
        headingDeg: 0,
        pitchDeg: 0,
        rollDeg: 0,
        scale: 1,
      });
      const baseModelMatrix =
        (metadata.baseModelMatrix as Cesium.Matrix4 | undefined) ??
        Cesium.Matrix4.clone(model.primitive.modelMatrix, new Cesium.Matrix4());

      metadata.baseModelMatrix = baseModelMatrix;
      metadata.localOffsetMeters = {
        eastMeters: offset.eastMeters,
        northMeters: offset.northMeters,
        upMeters: offset.upMeters,
        headingDegrees: offset.headingDegrees ?? 0,
        pitchDegrees: offset.pitchDegrees ?? 0,
        rollDegrees: offset.rollDegrees ?? 0,
      };

      const baseAnchorPosition =
        (metadata.baseAnchorPosition as Cesium.Cartesian3 | undefined) ??
        this.resolveModelBaseAnchorPosition(model.primitive, baseModelMatrix);

      if (!baseAnchorPosition) {
        console.warn(
          `[CesiumManager] Impossible de déterminer le point d'ancrage du modèle "${id}" pour appliquer un décalage local`,
        );
        return;
      }

      metadata.baseAnchorPosition = baseAnchorPosition;
      const nextMatrix = this.buildAlignedModelMatrix(
        baseModelMatrix,
        baseAnchorPosition,
        offset,
      );
      model.primitive.modelMatrix = nextMatrix;

      if (model.terrainFlattenEnabled) {
        this.syncModelTerrainFlatten(id, offset);
      }

      this.requestRender();
    } catch (error) {
      console.error(
        `[CesiumManager] Erreur lors du repositionnement du modèle "${id}":`,
        error,
      );
    }
  }

  animateModelArrival(
    id: string,
    options: ModelArrivalAnimationOptions = {},
  ): void {
    void this.animateModelVerticalTransition(id, {
      direction: "in",
      durationMs: options.durationMs,
      verticalOffsetMeters: options.verticalOffsetMeters,
    }).catch((error) => {
      console.warn(
        `[CesiumManager] Animation d'arrivée ignorée pour "${id}":`,
        error,
      );
    });
  }

  async animateModelDeparture(
    id: string,
    options: ModelArrivalAnimationOptions = {},
  ): Promise<void> {
    await this.animateModelVerticalTransition(id, {
      direction: "out",
      durationMs: options.durationMs,
      verticalOffsetMeters: options.verticalOffsetMeters,
    });
  }

  private async animateModelVerticalTransition(
    id: string,
    options: ModelArrivalAnimationOptions & {
      direction: "in" | "out";
    },
  ): Promise<void> {
    const loadedModel = this.loadedModels.get(id);
    if (!loadedModel) {
      return;
    }

    if (!(loadedModel.primitive instanceof Cesium.Model) || !this.viewer) {
      return;
    }

    const primitive = loadedModel.primitive;
    if (primitive.isDestroyed?.()) {
      return;
    }

    const stopExistingAnimation = this.runningModelArrivalAnimations.get(id);
    if (stopExistingAnimation) {
      stopExistingAnimation();
      this.runningModelArrivalAnimations.delete(id);
    }

    const durationMs = Math.max(250, options.durationMs ?? 950);
    const verticalOffsetMeters = Math.max(
      10,
      options.verticalOffsetMeters ?? 80,
    );

    const scene = this.viewer.scene;
    const baseMatrix = Cesium.Matrix4.clone(
      primitive.modelMatrix,
      new Cesium.Matrix4(),
    );
    const modelOrigin = this.resolveModelBaseAnchorPosition(
      primitive,
      baseMatrix,
    );
    const modelOriginMagnitude = modelOrigin
      ? Cesium.Cartesian3.magnitude(modelOrigin)
      : 0;

    if (
      !modelOrigin ||
      !Number.isFinite(modelOrigin.x) ||
      !Number.isFinite(modelOrigin.y) ||
      !Number.isFinite(modelOrigin.z) ||
      modelOriginMagnitude < 1_000_000
    ) {
      console.warn(
        `[CesiumManager] Animation verticale ignorée pour "${id}": origine du modèle indisponible ou invalide.`,
        {
          modelOrigin,
          modelOriginMagnitude,
        },
      );
      return;
    }

    const upDirection = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(
      modelOrigin,
      new Cesium.Cartesian3(),
    );

    if (
      !upDirection ||
      !Number.isFinite(upDirection.x) ||
      !Number.isFinite(upDirection.y) ||
      !Number.isFinite(upDirection.z)
    ) {
      console.warn(
        `[CesiumManager] Animation verticale ignorée pour "${id}": normale géodésique introuvable.`,
        {
          modelOrigin,
        },
      );
      return;
    }

    const verticalTranslation = Cesium.Cartesian3.multiplyByScalar(
      upDirection,
      verticalOffsetMeters,
      new Cesium.Cartesian3(),
    );
    const startMatrix = Cesium.Matrix4.multiplyByTranslation(
      baseMatrix,
      verticalTranslation,
      new Cesium.Matrix4(),
    );
    if (options.direction === "in") {
      primitive.modelMatrix = startMatrix;
    }

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const startTime = performance.now();

    let removePostRenderListener: (() => void) | null = null;
    await new Promise<void>((resolve) => {
      const stopAnimation = () => {
        if (removePostRenderListener) {
          removePostRenderListener();
          removePostRenderListener = null;
        }
        this.runningModelArrivalAnimations.delete(id);
        resolve();
      };

      removePostRenderListener = scene.postRender.addEventListener(() => {
        if (primitive.isDestroyed?.()) {
          stopAnimation();
          return;
        }

        const elapsedMs = performance.now() - startTime;
        const rawProgress = Math.max(0, Math.min(1, elapsedMs / durationMs));
        const easedProgress = easeOutCubic(rawProgress);
        const translationProgress =
          options.direction === "in" ? 1 - easedProgress : easedProgress;
        const currentOffset = verticalOffsetMeters * translationProgress;
        const frameMatrix = Cesium.Matrix4.multiplyByTranslation(
          baseMatrix,
          Cesium.Cartesian3.multiplyByScalar(
            upDirection,
            currentOffset,
            new Cesium.Cartesian3(),
          ),
          new Cesium.Matrix4(),
        );

        primitive.modelMatrix = frameMatrix;

        if (rawProgress >= 1) {
          primitive.modelMatrix =
            options.direction === "in" ? baseMatrix : startMatrix;
          const localOffsetMeters = loadedModel.metadata?.localOffsetMeters as
            | LocalModelAlignment
            | undefined;
          if (
            options.direction === "in" &&
            localOffsetMeters &&
            (localOffsetMeters.eastMeters !== 0 ||
              localOffsetMeters.northMeters !== 0 ||
              localOffsetMeters.upMeters !== 0 ||
              (localOffsetMeters.headingDegrees ?? 0) !== 0 ||
              (localOffsetMeters.pitchDegrees ?? 0) !== 0 ||
              (localOffsetMeters.rollDegrees ?? 0) !== 0)
          ) {
            this.setModelLocalOffset(id, localOffsetMeters);
          }
          this.requestRender();
          stopAnimation();
          return;
        }

        this.requestRender();
      });

      this.runningModelArrivalAnimations.set(id, stopAnimation);
      this.requestRender();
    });
  }

  /**
   * Récupère les modèles chargés
   */
  getLoadedModels(): Map<string, LoadedModel> {
    return this.loadedModels;
  }

  updateBasemapLightingOptions(
    predicate: (model: LoadedModel) => boolean,
    basemapLighting: BasemapLightingOptions,
  ): void {
    let updated = false;

    for (const model of this.loadedModels.values()) {
      if (!predicate(model)) {
        continue;
      }

      appliquerParametresEclairageBasemap(
        model.primitive,
        basemapLighting,
        true,
      );
      updated = true;
    }

    if (updated) {
      this.requestRender();
    }
  }

  updateSolarLightingOptions(
    predicate: (model: LoadedModel) => boolean,
    solarLighting: SolarDrivenLightingOptions,
  ): void {
    let updated = false;

    for (const model of this.loadedModels.values()) {
      if (!predicate(model)) {
        continue;
      }

      appliquerParametresEclairageSolaireModele(
        model.primitive,
        solarLighting,
        true,
      );
      updated = true;
    }

    if (updated) {
      this.requestRender();
    }
  }

  /**
   * Récupère un modèle spécifique
   */
  getModel(id: string): LoadedModel | undefined {
    return this.loadedModels.get(id);
  }

  /**
   * Anime la caméra
   */
  animateCamera(options: CameraOptions): void {
    if (!this.viewer) return;

    const {
      position,
      heading = 0,
      pitch = -Math.PI / 4,
      roll = 0,
      duration = 0,
    } = options;

    if (position) {
      this.viewer.camera.flyTo({
        destination: position,
        duration,
        orientation: {
          heading: Cesium.Math.toRadians(heading),
          pitch: Cesium.Math.toRadians(pitch),
          roll: Cesium.Math.toRadians(roll),
        },
      });
    } else {
      this.viewer.camera.setView({
        orientation: {
          heading: Cesium.Math.toRadians(heading),
          pitch: Cesium.Math.toRadians(pitch),
          roll: Cesium.Math.toRadians(roll),
        },
      });
    }
  }

  /**
   * Positionne la caméra à partir de coordonnées géographiques
   * @param latitude - Latitude en degrés
   * @param longitude - Longitude en degrés
   * @param cameraHeight - Hauteur de la caméra au-dessus du sol en mètres
   * @param duration - Durée de l'animation en secondes (0 = instantané)
   */
  flyToLocation(
    latitude: number,
    longitude: number,
    cameraHeight: number,
    duration: number = 0,
    pitchRadians: number = Cesium.Math.toRadians(
      CesiumManager.INITIAL_CAMERA_PITCH_DEGREES,
    ),
  ): void {
    if (!this.viewer) return;

    if (duration > 0) {
      this.viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          longitude,
          latitude,
          cameraHeight,
        ),
        duration,
        orientation: {
          heading: CesiumManager.INITIAL_CAMERA_HEADING,
          pitch: pitchRadians,
          roll: 0.0,
        },
      });
    } else {
      this.applyInitialCameraView(
        latitude,
        longitude,
        cameraHeight,
        pitchRadians,
      );
    }

    // Forcer un rendu
    this.requestRender();
  }

  /**
   * Réinitialise la vue (home)
   */
  resetView(): void {
    if (!this.viewer) return;
    this.viewer.camera.flyHome(0);
  }
}

export default CesiumManager;

// Exporter CesiumManager globalement pour accès via console
if (typeof window !== "undefined") {
  (window as any).CesiumManager = CesiumManager;
}
